import { useRef, useState } from "react";
import { api } from "../lib/api";
import { Paperclip, X, FileText, ImageIcon, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

export function isImage(ct) {
  return typeof ct === "string" && ct.startsWith("image/");
}

/**
 * MessageAttachments – renders attachment tiles on a chat message.
 */
export function MessageAttachments({ attachments, side = "left" }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className={`mt-2 flex flex-wrap gap-2 ${side === "right" ? "justify-end" : ""}`} data-testid="msg-attachments">
      {attachments.map((a) => {
        const url = `${API}/files/${a.file_id}`;
        if (isImage(a.content_type)) {
          return (
            <a key={a.file_id} href={url} target="_blank" rel="noopener noreferrer"
               className="block relative group" data-testid={`att-img-${a.file_id}`}>
              <img src={url} alt={a.original_filename || "allegato"}
                   className="max-w-[180px] max-h-[180px] rounded-md border border-slate-200 bg-white object-cover"/>
            </a>
          );
        }
        return (
          <a key={a.file_id} href={url} target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 px-3 py-2 bg-white/95 border border-slate-200 rounded-md hover:border-blue-400 hover:shadow-sm transition text-slate-800 max-w-[240px]"
             data-testid={`att-file-${a.file_id}`}>
            <FileText className="w-5 h-5 text-blue-800 shrink-0"/>
            <span className="text-xs truncate flex-1" title={a.original_filename}>{a.original_filename || "File"}</span>
            <Download className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
          </a>
        );
      })}
    </div>
  );
}

/**
 * AttachButton – shows a paperclip that opens the file picker.
 * Returns an array of uploaded {file_id, original_filename, content_type, size} via onUploaded.
 */
export function AttachButton({ onUploaded, disabled, testId = "chat-attach-btn" }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: max 15MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      if (uploaded.length) onUploaded && onUploaded(uploaded);
    } catch (e) {
      toast.error("Upload fallito");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input ref={ref} type="file" multiple className="hidden"
             accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
             onChange={(e) => handleFiles(Array.from(e.target.files || []))}
             data-testid={`${testId}-input`}/>
      <button type="button" onClick={() => ref.current?.click()} disabled={disabled || uploading}
              className="p-2 rounded-md text-slate-600 hover:text-blue-800 hover:bg-slate-100 disabled:opacity-50"
              title="Allega file" data-testid={testId}>
        {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Paperclip className="w-5 h-5"/>}
      </button>
    </>
  );
}

/**
 * PendingAttachments – shows tiles of files staged before sending; supports removal.
 */
export function PendingAttachments({ items, onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 pt-2" data-testid="pending-attachments">
      {items.map((a) => (
        <div key={a.file_id} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-xs" data-testid={`pending-${a.file_id}`}>
          {isImage(a.content_type) ? <ImageIcon className="w-3.5 h-3.5 text-blue-800"/> : <FileText className="w-3.5 h-3.5 text-blue-800"/>}
          <span className="max-w-[140px] truncate" title={a.original_filename}>{a.original_filename}</span>
          <button onClick={() => onRemove(a.file_id)} className="text-slate-500 hover:text-red-600" data-testid={`pending-remove-${a.file_id}`}>
            <X className="w-3.5 h-3.5"/>
          </button>
        </div>
      ))}
    </div>
  );
}
